import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PlusIcon,
  EyeIcon,
  UsersIcon,
  ChartBarIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon
} from '@heroicons/react/24/outline';
import { Campaign, Match } from '../../types';
import { graphqlApi } from '../../services/graphql';
import { useAuthStore } from '../../store/authStore';
import { Spinner } from '../common/Spinner';
import { CampaignCard } from '../campaigns/CampaignCard';
import { CampaignDetailsModal } from '../campaigns/CampaignDetailsModal';
import { MatchCard } from '../matches/MatchCard';
import CampaignGridCard from '../campaigns/CampaignGridCard';
import { useCampaignApplicationStats } from '../../hooks/useCampaignApplicationStats';
import { toast } from 'react-hot-toast';

export const BrandDashboard: React.FC = () => {
  const { t } = useTranslation('dashboard');
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [matches, setMatches] = useState<Match[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCampaign, setSelectedCampaign] = useState<Campaign | null>(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const applicationStats = useCampaignApplicationStats(user?.hostId);
  const [stats, setStats] = useState({
    totalCampaigns: 0,
    activeCampaigns: 0,
    totalMatches: 0,
    pendingMatches: 0
  });

  useEffect(() => {
    if (user?.userId) {
      loadDashboardData();
    }
  }, [user?.userId]);

  const loadDashboardData = async () => {
    if (!user?.userId) return;

    try {
      setIsLoading(true);

      // Prefer hostId from auth store (populated by directAuth.getCurrentUser on login/refresh).
      // Only hit GraphQL if it's missing — avoids the duplicate getHostProfileByUserId call.
      let hostId: string | null | undefined = user.hostId;
      if (!hostId) {
        const hostProfileResponse = await graphqlApi.getHostProfileByUserId(user.userId);
        hostId = hostProfileResponse.success ? hostProfileResponse.data?.hostId : null;
      }

      // Single query using getCampaignsByHost
      let allUserCampaigns: Campaign[] = [];

      if (hostId) {
        const response = await graphqlApi.getCampaignsByHost(hostId);
        if (response.success && response.data?.campaigns) {
          allUserCampaigns = response.data.campaigns;
        }
      }

      // Sort by creation date (newest first)
      const sortedCampaigns = [...allUserCampaigns].sort((a, b) =>
        new Date(b.createdAt || '').getTime() - new Date(a.createdAt || '').getTime()
      );

      setCampaigns(sortedCampaigns.slice(0, 6));
      setStats(prev => ({
        ...prev,
        totalCampaigns: allUserCampaigns.length,
        activeCampaigns: allUserCampaigns.filter(c => c.status === 'active').length
      }));

      // TODO: Implement matches loading once GraphQL resolver is ready
      // Load recent applications for the dashboard
      try {
        if (hostId) {
          console.log('🔍 Debug - Loading recent applications for hostId:', hostId);
          const recentAppsResponse = await graphqlApi.getRecentApplications(hostId, 5, 30); // Last 5 applications from 30 days

          if (recentAppsResponse.success && recentAppsResponse.data?.matches) {
            const matchData = recentAppsResponse.data.matches;
            setMatches(matchData.slice(0, 3)); // Show only latest 3 recent applications

            setStats(prev => ({
              ...prev,
            totalMatches: recentAppsResponse.data?.total || 0, // Use total count from API
              pendingMatches: matchData.filter(m => m.status === 'applied' || m.status === 'shortlisted' || m.status === 'requested').length
            }));
          }
        } else {
          console.log('⚠️ No hostId available, skipping recent applications load');
        }
      } catch (error) {
        setStats(prev => ({
          ...prev,
          totalMatches: 0,
          pendingMatches: 0
        }));
      }
    } catch (error: any) {
      console.error('Error loading dashboard data:', error);
      toast.error(t('loadError'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleViewDetails = (campaign: Campaign) => {
    navigate(`/campaigns/${campaign.campaignId}/detail`);
  };

  const handleEditCampaign = (campaign: Campaign) => {
    // Navigate to edit campaign page or open edit modal
    // For now, show details modal
    handleViewDetails(campaign);
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Spinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded-lg">
              <ChartBarIcon className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">{t('stats.totalCampaigns')}</h3>
              <p className="text-2xl font-bold text-gray-900">{stats.totalCampaigns}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded-lg">
              <EyeIcon className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">{t('stats.activeCampaigns')}</h3>
              <p className="text-2xl font-bold text-gray-900">{stats.activeCampaigns}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-purple-100 rounded-lg">
              <UsersIcon className="h-6 w-6 text-purple-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">{t('stats.totalMatches')}</h3>
              <p className="text-2xl font-bold text-gray-900">{stats.totalMatches}</p>
            </div>
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center">
            <div className="p-2 bg-orange-100 rounded-lg">
              <ClockIcon className="h-6 w-6 text-orange-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">{t('stats.pendingMatches')}</h3>
              <p className="text-2xl font-bold text-gray-900">{stats.pendingMatches}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <h2 className="text-xl font-bold text-gray-900 mb-4">{t('quickActions')}</h2>
        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <Link
            to="/campaigns/register"
            className="flex items-center p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
          >
            <PlusIcon className="h-6 w-6 text-blue-600 mr-3" />
            <span className="font-medium text-blue-900">{t('actions.createCampaign')}</span>
          </Link>
        </div>
      </div>

      {/* Recent Campaigns — Grid Cards */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">{t('recentCampaigns')}</h2>
          <Link
            to="/campaigns"
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            {t('viewAll')}
          </Link>
        </div>

        {campaigns.length === 0 ? (
          <div className="text-center py-8 bg-white rounded-lg border border-gray-200">
            <ChartBarIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">{t('noCampaigns')}</p>
            <Link
              to="/campaigns/register"
              className="mt-4 inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              {t('actions.createCampaign')}
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {campaigns.map((campaign, idx) => (
              <CampaignGridCard
                key={campaign.campaignId}
                campaign={campaign}
                index={idx}
                linkPrefix="/campaigns"
                userType={user?.userType}
                applicationStats={applicationStats[campaign.campaignId]}
              />
            ))}
          </div>
        )}
      </div>

      {/* Recent Matches */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">{t('recentApplications')}</h2>
          <Link
            to="/matches"
            className="text-blue-600 hover:text-blue-800 text-sm font-medium"
          >
            {t('viewAll')}
          </Link>
        </div>

        {matches.length === 0 ? (
          <div className="text-center py-8">
            <UsersIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-500">{t('noApplications')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {matches.map((match) => (
              <MatchCard key={match.matchId} match={match} />
            ))}
          </div>
        )}
      </div>

      {/* Campaign Details Modal */}
      {selectedCampaign && (
        <CampaignDetailsModal
          campaign={selectedCampaign}
          isOpen={showDetailsModal}
          onClose={() => {
            setShowDetailsModal(false);
            setSelectedCampaign(null);
          }}
        />
      )}
    </div>
  );
};
